import { useTranslation } from 'react-i18next'

import { VehiclesCard, useVehiclesForm } from '#/features/order-create'
import type { VehiclesFormData } from '#/features/order-create'
import type { VehicleResponse } from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import { TruckNopIcon } from '#/shared/ui/icons'
import { EditFooter } from './edit-footer'
import { SectionEditButton } from './section-edit-button'

function focusFirstInvalidField(): void {
  const el = document.querySelector<HTMLElement>('[aria-invalid="true"]')
  if (!el) return
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  if (typeof el.focus === 'function') el.focus({ preventScroll: true })
}

interface VehicleSectionProps {
  vehicles: VehicleResponse[]
  editing: boolean
  onEditStart: () => void
  onCancel: () => void
  onSave: (data: VehiclesFormData) => void | Promise<void>
  defaultValues: VehiclesFormData
  readOnly?: boolean
  editClassName?: string
  /** Drops the section's mx-2.5 / px-2.5 insets for flush sidebar layouts. */
  flush?: boolean
}

function VehicleRow({
  vehicle,
  flush,
}: {
  vehicle: VehicleResponse
  flush?: boolean
}) {
  const name = `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim()
  return (
    <div className={cn('flex h-8 items-center', !flush && 'px-2.5')}>
      <div className="flex h-full flex-1 items-center gap-1.25">
        <span className="text-sm text-gray-900">{name}</span>
        {vehicle.type && (
          <span className="flex h-5 items-center rounded-md border border-gray-200 bg-gray-100 px-1.25 text-xs uppercase text-gray-900">
            {vehicle.type}
          </span>
        )}
        {vehicle.vin && (
          <span className="max-md:hidden text-xs text-gray-500">
            {vehicle.vin}
          </span>
        )}
        {vehicle.is_inoperable && (
          <TruckNopIcon className="size-4 shrink-0 text-destructive" />
        )}
      </div>
    </div>
  )
}

function VehicleSectionView({
  vehicles,
  onEdit,
  flush,
}: {
  vehicles: VehicleResponse[]
  onEdit?: () => void
  flush?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'group/section relative flex flex-col rounded-lg',
        // flush (sidebar): pad inside the parent's inset so the hover bg keeps
        // an even margin from the panel edges instead of bleeding to them.
        flush ? 'px-2.5' : 'mx-2.5',
        onEdit && 'hover:bg-gray-100',
      )}
    >
      {onEdit && (
        <SectionEditButton
          label={t('shipmentCard.editVehicle')}
          onClick={onEdit}
        />
      )}
      <div
        className={cn(
          'flex h-6 items-center justify-between text-xs text-gray-500',
          !flush && 'px-2.5',
        )}
      >
        <span className="uppercase">{t('shipmentCard.vehicle')}</span>
        <span>
          {t('shipmentCard.vehicleCount', { count: vehicles.length })}
        </span>
      </div>
      {vehicles.length === 0 ? (
        <div className={cn('flex h-8 items-center', !flush && 'px-2.5')}>
          <span className={cn('text-sm text-gray-500', !flush && 'px-1.25')}>
            {t('shipmentCard.noVehicles')}
          </span>
        </div>
      ) : (
        vehicles.map((v) => <VehicleRow key={v.id} vehicle={v} flush={flush} />)
      )}
    </div>
  )
}

function VehicleSectionEdit({
  defaultValues,
  onCancel,
  onSave,
  editClassName,
  flush,
}: {
  defaultValues: VehiclesFormData
  onCancel: () => void
  onSave: (data: VehiclesFormData) => void | Promise<void>
  editClassName?: string
  flush?: boolean
}) {
  const {
    control,
    register,
    setValue,
    errors,
    fields,
    addVehicle,
    removeVehicle,
    trigger,
    getValues,
    isSubmitting,
  } = useVehiclesForm({ defaultValues })

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const ok = await trigger()
    if (!ok) {
      focusFirstInvalidField()
      return
    }
    await onSave(getValues())
  }

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      className={cn(
        'flex flex-col rounded-lg bg-gray-100',
        !flush && 'mx-2.5',
        editClassName,
      )}
    >
      <VehiclesCard
        bare
        control={control}
        register={register}
        setValue={setValue}
        errors={errors}
        fields={fields}
        onAdd={addVehicle}
        onRemove={removeVehicle}
      />
      <EditFooter
        onCancel={onCancel}
        saveType="submit"
        disabled={isSubmitting}
      />
    </form>
  )
}

export function VehicleSection({
  vehicles,
  editing,
  onEditStart,
  onCancel,
  onSave,
  defaultValues,
  readOnly,
  editClassName,
  flush,
}: VehicleSectionProps) {
  if (editing && !readOnly) {
    return (
      <VehicleSectionEdit
        defaultValues={defaultValues}
        onCancel={onCancel}
        onSave={onSave}
        editClassName={editClassName}
        flush={flush}
      />
    )
  }
  return (
    <VehicleSectionView
      vehicles={vehicles}
      onEdit={readOnly ? undefined : onEditStart}
      flush={flush}
    />
  )
}
